import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for video recording with preview
 * @param {Function} onComplete - Callback when recording is complete, receives blob
 * @returns {Object} Recording state and control functions
 */
export default function useVideoRecording({ onComplete }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(null);

  // Connect stream to video element when recording starts
  // This handles the case where the stream is passed in before the video element mounts
  useEffect(() => {
    if (isRecording && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isRecording]);

  // Start recording (optionally accepts an existing stream to avoid camera restart delay)
  const start = useCallback(async (existingStream = null) => {
    try {
      const stream = existingStream || await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;

      // Setup video preview - try immediately, or wait for element to mount
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      } else {
        // Video element not mounted yet (happens when passing existing stream)
        // The useEffect will connect it once the component renders
        requestAnimationFrame(() => {
          if (videoRef.current && !videoRef.current.srcObject) {
            videoRef.current.srcObject = stream;
          }
        });
      }

      // Setup MediaRecorder
      let options = {};
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        options = { mimeType: 'video/webm;codecs=vp9' };
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        options = { mimeType: 'video/webm' };
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        // Safari fallback
        options = { mimeType: 'video/mp4' };
      }
      console.log('MediaRecorder options:', options);

      const recorder = new MediaRecorder(stream, options);
      console.log('MediaRecorder created:', {
        state: recorder.state,
        mimeType: recorder.mimeType
      });

      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.onstart = () => {
        console.log('✓ MediaRecorder STARTED, state:', recorder.state);
        startTimeRef.current = Date.now();
      };

      recorder.ondataavailable = (e) => {
        console.log(`📊 Data available: ${e.data.size} bytes, total chunks: ${chunksRef.current.length + 1}`);
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          console.log(`  ✓ Chunk added. Total chunks now: ${chunksRef.current.length}`);
        } else {
          console.warn('  ⚠ Empty data chunk received!');
        }
      };

      recorder.onerror = (e) => {
        console.error('❌ MediaRecorder ERROR:', e);
      };

      recorder.onstop = async () => {
        console.log('⏹ MediaRecorder STOPPED');
        console.log(`  Total chunks collected: ${chunksRef.current.length}`);

        // Use the actual MIME type that MediaRecorder used (fixes Safari MP4 support)
        // Extract base MIME type without codec details to avoid multipart form data corruption
        const baseMimeType = recorder.mimeType ? recorder.mimeType.split(';')[0] : 'video/webm';
        const blob = new Blob(chunksRef.current, {
          type: baseMimeType
        });
        console.log(`📦 Recording blob created:`, {
          size: blob.size,
          type: blob.type,
          chunks: chunksRef.current.length
        });

        // Cleanup
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }

        setIsRecording(false);
        setIsPaused(false);

        if (blob.size > 0 && onComplete) {
          const durationSeconds = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : null;
          await onComplete(blob, durationSeconds);
        } else {
          console.error('❌ Recording blob is EMPTY!');
          alert('Recording failed - no data captured.');
        }
      };

      // Start recording
      try {
        recorder.start(1000); // Collect data every second
        console.log(`🎬 Started video recording with timeslice=1000ms`);
      } catch (startError) {
        console.error('Failed to start with timeslice, trying without:', startError);
        recorder.start();
        console.log(`🎬 Started video recording without timeslice`);
      }

      setIsRecording(true);
      setIsPaused(false);
    } catch (error) {
      console.error('❌ Error starting video recording:', error);
      alert('Failed to access camera/microphone: ' + error.message);
    }
  }, [onComplete]);

  // Stop recording
  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  // Pause recording
  const pause = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.pause();
      setIsPaused(true);
    }
  }, []);

  // Resume recording
  const resume = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'paused') {
      recorderRef.current.resume();
      setIsPaused(false);
    }
  }, []);

  // Callback ref that connects stream when video element mounts
  const setVideoRef = useCallback((element) => {
    videoRef.current = element;
    // Connect stream immediately when element becomes available
    if (element && streamRef.current && !element.srcObject) {
      element.srcObject = streamRef.current;
    }
  }, []);

  return {
    isRecording,
    isPaused,
    videoRef,
    setVideoRef,  // Use this as ref for guaranteed stream connection
    start,
    stop,
    pause,
    resume
  };
}
